<template>
  <div class="threaded-comments">
    <!-- Comment Input -->
    <CommentInput
      :user-data="userData"
      :post-id="postId"
      @comment-submitted="handleCommentSubmitted"
    />

    <!-- Loading State -->
    <div v-if="loading" class="loading-comments">
      <div class="spinner"></div>
    </div>

    <!-- Comments List -->
    <div v-else class="comments-list">
      <CommentItem
        v-for="comment in visibleComments"
        :key="comment.id"
        :comment="comment"
        :user-data="userData"
        :post-id="postId"
        :depth="0"
        @reply-submitted="handleReplySubmitted"
        @comment-deleted="handleCommentDeleted"
        @reaction-toggled="handleReactionToggled"
      />

      <!-- Load More Comments -->
      <button
        v-if="hasMore"
        class="load-more-btn"
        @click="loadMoreComments"
      >
        View more comments ({{ comments.length - visibleCount }})
      </button>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && comments.length === 0" class="empty-comments">
      <p>No comments yet. Be the first to comment!</p>
    </div>
  </div>
</template>

<script>
import { PostComments } from "@/services/team-posts-service";
import CommentInput from "./CommentInput.vue";
import CommentItem from "./CommentItem.vue";

export default {
  name: "ThreadedComments",
  components: {
    CommentInput,
    CommentItem,
  },
  props: {
    postId: {
      type: [Number, String],
      required: true,
    },
    userData: {
      type: Object,
      required: true,
    },
  },
  emits: ["comment-added"],
  data() {
    return {
      // Single source of truth — the full comment list. Pagination is now
      // handled client-side via visibleCount so we keep mutations (replies,
      // deletes, reactions) consistent across the visible/hidden boundary.
      comments: [],
      loading: false,
      visibleCount: 3,
      pageSize: 3,
    };
  },
  computed: {
    visibleComments() {
      return this.comments.slice(0, this.visibleCount);
    },
    hasMore() {
      return this.comments.length > this.visibleCount;
    },
  },
  mounted() {
    this.loadComments();
  },
  methods: {
    async loadComments() {
      this.loading = true;
      try {
        const response = await PostComments.getComments(this.postId);
        if (response.data.success) {
          this.comments = [...response.data.comments];
        }
      } catch (error) {
        console.error("Error loading comments:", error);
      } finally {
        this.loading = false;
      }
    },

    loadMoreComments() {
      // Reveal the next page of already-loaded comments. The previous
      // implementation re-fetched the entire list and replaced the array,
      // so users could only ever see "first 3 + everything" with no
      // intermediate pagination.
      this.visibleCount = Math.min(
        this.visibleCount + this.pageSize,
        this.comments.length,
      );
    },

    handleCommentSubmitted(newComment) {
      this.comments = [newComment, ...this.comments];
      this.$emit("comment-added");
    },

    handleReplySubmitted({ parentComment, newReply }) {
      // Find parent and add reply
      this.addReplyToComment(this.comments, parentComment.id, newReply);
      this.$emit("comment-added");
    },

    addReplyToComment(comments, parentId, reply) {
      for (let comment of comments) {
        if (comment.id === parentId) {
          if (!comment.replies) {
            comment.replies = [];
          }
          comment.replies = [...comment.replies, reply];
          return true;
        }
        if (comment.replies && comment.replies.length > 0) {
          if (this.addReplyToComment(comment.replies, parentId, reply)) {
            return true;
          }
        }
      }
      return false;
    },

    handleCommentDeleted(commentId) {
      this.removeComment(this.comments, commentId);
    },

    removeComment(comments, commentId) {
      const index = comments.findIndex((c) => c.id === commentId);
      if (index !== -1) {
        comments.splice(index, 1);
        this.comments = [...this.comments]; // Trigger reactivity
        return true;
      }
      for (let comment of comments) {
        if (comment.replies && comment.replies.length > 0) {
          if (this.removeComment(comment.replies, commentId)) {
            return true;
          }
        }
      }
      return false;
    },

    handleReactionToggled({ commentId, reactions }) {
      this.updateCommentReactions(this.comments, commentId, reactions);
    },

    updateCommentReactions(comments, commentId, reactions) {
      for (let comment of comments) {
        if (comment.id === commentId) {
          comment.reactions = reactions;
          this.comments = [...this.comments]; // Trigger reactivity
          return true;
        }
        if (comment.replies && comment.replies.length > 0) {
          if (this.updateCommentReactions(comment.replies, commentId, reactions)) {
            return true;
          }
        }
      }
      return false;
    },
  },
};
</script>

<style scoped>
.threaded-comments {
  padding: 12px 16px;
  border-top: 1px solid #e4e6eb;
}

.loading-comments {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #e4e6eb;
  border-top-color: #1877f2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.comments-list {
  margin-top: 12px;
}

.load-more-btn {
  display: block;
  width: 100%;
  padding: 8px;
  border: none;
  background: none;
  color: #65676b;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}

.load-more-btn:hover {
  text-decoration: underline;
}

.empty-comments {
  padding: 16px 0;
  text-align: center;
  color: #65676b;
  font-size: 14px;
}

.empty-comments p {
  margin: 0;
}
</style>
